import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import once from 'lodash/once';
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
  useMergeRefs,
} from '@floating-ui/react';
import styled from 'astroturf/react';
import { Button } from 'js/design_system/Button';
import { TextButton } from 'js/design_system/TextButton';
import { Body } from 'js/design_system/Body';
import { Heading } from 'js/design_system/Heading';
import { Icon } from 'js/design_system/Icon';
import {
  BatchImpressionReporter,
  ImpressionEntity,
  watchImpressions,
  getImpressionId,
  sendBatchImpressions,
} from 'js/utils/impressions/watchImpressions';
import trackClick from 'js/utils/trackClick';
import { write as writeEvent } from 'js/core_webpack/arbitrary_events';
import { captureException } from '@sentry/browser';
import {
  CitationMetadata,
  inlineCitation,
  wordCount,
} from 'js/journals/article/citationFormatting';

import {
  CITABLE_CONTENT_SELECTOR,
  FORMULA_SELECTOR,
  isCitableSelection,
  selectionContainsFormula,
} from 'js/journals/article/citationSelection';

const citationImpressions = once(
  () => new BatchImpressionReporter(sendBatchImpressions),
);

type CitationImpressionProps = {
  source: string;
  entity_id: number;
  extra: object;
  className?: string;
  children: React.ReactNode;
};

function CitationImpression({
  source,
  entity_id,
  extra,
  className,
  children,
}: CitationImpressionProps) {
  const element = useRef<HTMLDivElement>(null);
  const recorded = useRef(false);
  const impressionData = useRef({ source, entity_id, extra }).current;
  useEffect(() => {
    if (!element.current || recorded.current) return undefined;
    // Each caption and popup opening needs its own impression, even on the same article.
    const observer = new IntersectionObserver((entries) => {
      if (recorded.current || !entries.some((entry) => entry.isIntersecting))
        return;
      recorded.current = true;
      const impression = {
        impression_id: getImpressionId(),
        impression_time: new Date(),
        hit_id: window.Aedu?.hit_data?.id,
        ...impressionData,
        entity_type: ImpressionEntity.Work,
      };
      citationImpressions().registerView(impression);
      observer.disconnect();
    });
    observer.observe(element.current);
    return () => observer.disconnect();
  }, [impressionData]);
  return (
    <div ref={element} className={className}>
      {children}
    </div>
  );
}

const Panel = styled.div`
  width: min(640px, calc(100vw - 24px));
  max-height: calc(100dvh - 24px);
  overflow: auto;
  z-index: 100;
  background: var(--background-white);
  color: var(--text-primary);
  border: 1px solid var(--stroke-dark);
  border-radius: 16px;
  box-shadow: 0 12px 40px rgb(0 0 0 / 15%);
  padding: 16px;

  @media (max-width: 480px) {
    padding: 12px;
  }

  @media print {
    display: none;
  }
`;
const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;
const Title = styled(Heading).attrs({ as: 'h2', size: 'xs' })`
  flex: 1;
  margin: 0;
`;
const CaptionActions = styled(CitationImpression)`
  -webkit-user-select: none;
  user-select: none;
  margin: 8px 0 24px;

  @media print {
    display: none;
  }
`;
const Status = styled(Body).attrs({ as: 'div', size: 'xs' })`
  margin: 8px 0 0;

  &:empty {
    margin: 0;
  }
`;
const Preview = styled(Body).attrs({ as: 'div', size: 'md' })`
  background: var(--background-beige-light);
  border-radius: 12px;
  padding: 12px;
  margin: 0 0 12px;
  max-height: 120px;
  overflow: auto;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;
const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
`;

type Target = {
  text: string;
  containsFormula: boolean;
  kind: 'selection' | 'table' | 'figure';
  label?: string;
  anchor: Range | HTMLElement;
};
type CaptionHost = {
  host: HTMLElement;
  target: Target;
  row?: HTMLElement;
  tableAction?: Element;
};
type Props = {
  metadata: CitationMetadata;
  bibtex: string;
  publishedLetterId: number;
  loggedIn: boolean;
};

export default function SelectionCitation({
  metadata,
  bibtex,
  publishedLetterId,
  loggedIn,
}: Props) {
  const [target, setTarget] = useState<Target | null>(null);
  const [hosts, setHosts] = useState<CaptionHost[]>([]);
  const [status, setStatus] = useState('');
  const [manualCopy, setManualCopy] = useState('');
  const panel = useRef<HTMLDivElement | null>(null);
  const interactingWithPanel = useRef(false);
  const returnFocus = useRef<HTMLElement | null>(null);
  const dismissedSelection = useRef<Range | null>(null);
  const closePopup = useCallback(() => {
    if (!panel.current) return;
    const selection = window.getSelection();
    dismissedSelection.current = selection?.rangeCount
      ? selection.getRangeAt(0).cloneRange()
      : null;
    if (panel.current?.contains(document.activeElement))
      returnFocus.current?.focus({ preventScroll: true });
    returnFocus.current = null;
    setTarget(null);
  }, []);
  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-start',
    middleware: [offset(12), flip(), shift({ padding: 12, crossAxis: true })],
    whileElementsMounted: autoUpdate,
  });
  const panelRef = useMergeRefs([panel, refs.setFloating]);
  const citation = inlineCitation(metadata);
  const extra = {
    published_letter_id: publishedLetterId,
    logged_in: loggedIn,
    target: target?.kind,
    label: target?.label,
    word_count: target ? wordCount(target.text) : undefined,
  };

  const popupTitle = target?.label
    ? `Cite ${target.label}`
    : 'Cite this passage';

  useLayoutEffect(() => {
    if (!target) {
      interactingWithPanel.current = false;
      return;
    }
    const anchor = target.anchor;
    refs.setPositionReference({
      getBoundingClientRect: () => anchor.getBoundingClientRect(),
      contextElement:
        anchor instanceof HTMLElement
          ? anchor
          : anchor.startContainer.parentElement || undefined,
    });
    if (target.kind !== 'selection')
      panel.current?.focus({ preventScroll: true });
    setStatus('');
    setManualCopy('');
  }, [target, refs]);

  useEffect(() => {
    const article = document.querySelector('#sap-article-body');
    const root =
      document.querySelector<HTMLElement>(CITABLE_CONTENT_SELECTOR) ||
      article?.querySelector<HTMLElement>('.sap__table-wrap, .sap__fig');
    if (!article || !root) return undefined;
    const watcher = watchImpressions(root, {
      source: 'sap_citation_available',
      entity_id: metadata.id,
      entity_type: ImpressionEntity.Work,
      threshold: 0,
      extra: { published_letter_id: publishedLetterId, logged_in: loggedIn },
    });
    const created: CaptionHost[] = [];
    article
      .querySelectorAll<HTMLElement>('.sap__table-wrap, .sap__fig')
      .forEach((element) => {
        const caption = element.querySelector<HTMLElement>(
          ':scope > .sap__caption',
        );
        if (!caption?.textContent?.trim()) return;
        const kind = element.classList.contains('sap__table-wrap')
          ? 'table'
          : 'figure';
        const label =
          element.querySelector(':scope > .sap__label')?.textContent?.trim() ||
          (kind === 'table' ? 'Table' : 'Figure');
        const host = document.createElement('div');
        const tableAction = element.nextElementSibling;
        let row: HTMLElement | undefined;
        if (tableAction?.matches('[data-sap-table-cta]')) {
          row = document.createElement('div');
          row.className = 'sap__table-actions';
          host.className = 'sap__table-citation';
          tableAction.before(row);
          row.append(tableAction, host);
        } else {
          element.after(host);
        }
        created.push({
          host,
          row,
          tableAction: row ? (tableAction ?? undefined) : undefined,
          target: {
            text: caption.textContent.trim(),
            containsFormula: !!caption.querySelector(FORMULA_SELECTOR),
            kind,
            label,
            anchor: host,
          },
        });
      });
    setHosts(created);
    return () => {
      watcher.stop();
      created.forEach(({ host, row, tableAction }) => {
        host.remove();
        if (row && tableAction?.parentElement === row) row.before(tableAction);
        row?.remove();
      });
    };
  }, [metadata.id, publishedLetterId, loggedIn]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    let selecting = false;
    const readSelection = () => {
      if (
        selecting ||
        interactingWithPanel.current ||
        panel.current?.contains(document.activeElement)
      )
        return;
      const selection = window.getSelection();
      if (
        selection?.anchorNode &&
        panel.current?.contains(selection.anchorNode)
      )
        return;
      const roots = document.querySelectorAll(CITABLE_CONTENT_SELECTOR);
      if (
        !selection ||
        selection.isCollapsed ||
        selection.rangeCount !== 1 ||
        !roots.length
      ) {
        dismissedSelection.current = null;
        setTarget((current) =>
          current?.kind === 'selection' ? null : current,
        );
        return;
      }
      const range = selection.getRangeAt(0);
      const dismissed = dismissedSelection.current;
      if (
        dismissed &&
        range.compareBoundaryPoints(Range.START_TO_START, dismissed) === 0 &&
        range.compareBoundaryPoints(Range.END_TO_END, dismissed) === 0
      )
        return;
      dismissedSelection.current = null;
      const text = selection.toString().trim();
      if (!isCitableSelection(range, roots, text)) {
        setTarget(null);
        return;
      }
      const containsFormula = selectionContainsFormula(range);
      setTarget((current) =>
        current?.kind === 'selection' &&
        current.text === text &&
        current.containsFormula === containsFormula
          ? current
          : {
              text,
              containsFormula,
              kind: 'selection',
              anchor: range.cloneRange(),
            },
      );
    };
    const schedule = () => {
      clearTimeout(timer);
      timer = setTimeout(readSelection, 250);
    };
    const pointerDown = (event: PointerEvent) => {
      if (event.button !== 0 || panel.current?.contains(event.target as Node))
        return;
      interactingWithPanel.current = false;
      dismissedSelection.current = null;
      selecting = true;
      setTarget(null);
    };
    const pointerUp = () => {
      selecting = false;
      schedule();
    };
    const keyDown = (event: KeyboardEvent) => {
      if (!panel.current?.contains(event.target as Node))
        interactingWithPanel.current = false;
      if (event.key === 'Escape') {
        clearTimeout(timer);
        closePopup();
      }
      if (
        event.key === 'Tab' &&
        !event.shiftKey &&
        !event.altKey &&
        !event.ctrlKey &&
        !event.metaKey &&
        panel.current &&
        !panel.current.contains(event.target as Node)
      ) {
        const selection = window.getSelection();
        const roots = document.querySelectorAll(CITABLE_CONTENT_SELECTOR);
        if (
          selection &&
          !selection.isCollapsed &&
          selection.rangeCount === 1 &&
          isCitableSelection(
            selection.getRangeAt(0),
            roots,
            selection.toString(),
          )
        ) {
          event.preventDefault();
          returnFocus.current = document.activeElement as HTMLElement;
          panel.current.focus({ preventScroll: true });
        }
      }
    };
    document.addEventListener('selectionchange', schedule);
    document.addEventListener('pointerdown', pointerDown);
    document.addEventListener('pointerup', pointerUp);
    document.addEventListener('pointercancel', pointerUp);
    document.addEventListener('contextmenu', pointerUp);
    document.addEventListener('visibilitychange', pointerUp);
    window.addEventListener('blur', pointerUp);
    document.addEventListener('keydown', keyDown);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('selectionchange', schedule);
      document.removeEventListener('pointerdown', pointerDown);
      document.removeEventListener('pointerup', pointerUp);
      document.removeEventListener('pointercancel', pointerUp);
      document.removeEventListener('contextmenu', pointerUp);
      document.removeEventListener('visibilitychange', pointerUp);
      window.removeEventListener('blur', pointerUp);
      document.removeEventListener('keydown', keyDown);
    };
  }, [closePopup]);

  const copy = async (format: 'quote' | 'citation' | 'bibtex') => {
    if (!target || (format === 'quote' && target.containsFormula)) return;
    const text =
      format === 'bibtex'
        ? bibtex
        : format === 'citation'
          ? citation
          : `“${target.text}” ${citation}.`;
    const trackCopy = (outcome: 'click' | 'success' | 'failure') =>
      trackClick({
        source: `sap_citation_copy_${outcome}`,
        entityId: metadata.id,
        extra: { ...extra, format },
      });
    trackCopy('click');
    try {
      await navigator.clipboard.writeText(text);
      setManualCopy('');
      setStatus('Copied to clipboard');
      trackCopy('success');
    } catch {
      setManualCopy(text);
      setStatus('Could not copy. Please select and copy the text above.');
      trackCopy('failure');
      return;
    }
    void Promise.resolve()
      .then(async () =>
        writeEvent('SapCitationCopied', {
          work_id: metadata.id,
          published_letter_id: publishedLetterId,
          format,
          target: target.kind,
          label: target.label?.slice(0, 200) || null,
          selected_text: target.text
            .slice(0, 1000)
            .replace(/[\uD800-\uDBFF]$/, ''),
          selected_text_length: target.text.length,
          truncated: target.text.length > 1000,
        }),
      )
      .catch((error) => captureException(error));
  };

  return (
    <>
      {hosts.map(({ host, target: captionTarget }, index) =>
        createPortal(
          <CaptionActions
            source="sap_citation_caption"
            entity_id={metadata.id}
            extra={{
              published_letter_id: publishedLetterId,
              logged_in: loggedIn,
              target: captionTarget.kind,
              label: captionTarget.label,
            }}
          >
            <TextButton
              inline
              size={captionTarget.kind === 'table' ? 'lg' : 'sm'}
              aria-label={`Cite ${captionTarget.label}`}
              icon="format_quote"
              onClick={() => {
                window.getSelection()?.removeAllRanges();
                setTarget(captionTarget);
                trackClick({
                  source: 'sap_citation_caption_open',
                  entityId: metadata.id,
                  extra: {
                    published_letter_id: publishedLetterId,
                    logged_in: loggedIn,
                    target: captionTarget.kind,
                    label: captionTarget.label,
                  },
                });
              }}
            >
              Cite {captionTarget.label}
            </TextButton>
          </CaptionActions>,
          host,
          `citation-${index}`,
        ),
      )}
      {target &&
        createPortal(
          <Panel
            ref={panelRef}
            style={floatingStyles}
            role="dialog"
            aria-modal={false}
            tabIndex={-1}
            onFocusCapture={(event: React.FocusEvent) => {
              if (
                event.relatedTarget instanceof HTMLElement &&
                !event.currentTarget.contains(event.relatedTarget)
              )
                returnFocus.current = event.relatedTarget;
            }}
            aria-label={popupTitle}
            onPointerDown={(event: React.PointerEvent) => {
              interactingWithPanel.current = true;
              event.stopPropagation();
            }}
          >
            <CitationImpression
              key={
                target.kind === 'selection'
                  ? 'selection'
                  : `${target.kind}-${target.label}-${target.text}`
              }
              source="sap_citation_popup"
              entity_id={metadata.id}
              extra={extra}
            >
              <Header>
                <Icon name="format_quote" />
                <Title>{popupTitle}</Title>
                <TextButton
                  inline
                  size="sm"
                  icon="close"
                  aria-label="Close citation popup"
                  onClick={closePopup}
                />
              </Header>
              <Preview>
                {manualCopy ||
                  (target.containsFormula
                    ? 'This selection includes formulas. Copy the citation or BibTeX instead.'
                    : `“${target.text}” ${citation}.`)}
              </Preview>
              <Actions>
                <Button
                  size="sm"
                  disabled={target.containsFormula}
                  onClick={() => copy('quote')}
                >
                  Copy quote with citation
                </Button>
                <Actions>
                  <TextButton inline size="sm" onClick={() => copy('citation')}>
                    Citation only
                  </TextButton>
                  <TextButton inline size="sm" onClick={() => copy('bibtex')}>
                    BibTeX
                  </TextButton>
                </Actions>
              </Actions>
              <Status role="status" aria-live="polite">
                {status}
              </Status>
            </CitationImpression>
          </Panel>,
          document.body,
        )}
    </>
  );
}
